import { useEffect, useState } from 'preact/hooks';
import { Check, Upload, X } from 'lucide-preact';
import { api, ApiErr } from '@/lib/teaching/teacher-api';
import { errText } from '@/lib/teaching/slice2';
import {
  FIELD_LABEL, IMPORT_FIELDS, KIND_LABEL, ageText, daysUntil, shortDate,
  type Commitment, type CommitmentList, type Domain, type ImportField, type ImportPreview, type Kind, type Status, type Tab,
} from '@/lib/teaching/slice6';
import { TeachingPage, Card } from '@/components/teaching/ui';
import { Chip, TableCard, thCls, tdCls } from '@/components/teaching/kit';
import { Btn, ErrorLine, Field, inputCls, val } from '@/components/teaching/form';
import { Note, Tabs, selectCls, textareaCls } from '@/components/teaching/setup';

// Commitments (slice 6 §02): everything Aaqib needs to do, work and
// personal, in one list. Each row leads with its next action. A project is
// one row with its current next action; there are no subtask lists.

type DomainFilter = Domain | 'all';
const DOMAIN_OPTS: { id: DomainFilter; label: string }[] = [{ id: 'all', label: 'All' }, { id: 'work', label: 'Work' }, { id: 'personal', label: 'Personal' }];
const EMPTY: Record<Tab, string> = {
  now: 'Nothing here yet. Add the first thing on your mind above.',
  waiting: 'Nothing you are waiting on someone for.',
  later: 'Nothing parked for later.',
  done: 'Nothing finished yet.',
};

function DeadlineChip({ iso, done }: { iso: string; done: boolean }) {
  const d = daysUntil(iso);
  const tone = done ? 'neutral' : d < 0 ? 'reteach' : d <= 3 ? 'watch' : 'neutral';
  const when = d < 0 ? `${-d} day${d === -1 ? '' : 's'} overdue` : d === 0 ? 'due today' : d === 1 ? 'due tomorrow' : `due ${shortDate(iso)}`;
  return <Chip tone={tone} title={`Deadline ${shortDate(iso)}`}>{done ? shortDate(iso) : when}</Chip>;
}

export function Commitments() {
  const [tab, setTab] = useState<Tab>('now');
  const [domain, setDomain] = useState<DomainFilter>('all');
  const [data, setData] = useState<CommitmentList | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [quick, setQuick] = useState('');
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<Commitment | null>(null);
  const [importing, setImporting] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = async () => {
    try {
      const q = new URLSearchParams({ tab, ...(domain !== 'all' ? { domain } : {}) });
      setData(await api<CommitmentList>('GET', `/api/t/commitments?${q}`));
      setErr(null);
    } catch (e) { setErr(errText(e)); }
  };
  useEffect(() => { void load(); }, [tab, domain]);

  async function add(e: Event) {
    e.preventDefault();
    const title = quick.trim();
    if (!title || busy) return;
    setBusy(true); setNote(null);
    try {
      await api('POST', '/api/t/commitments', { title, domain: domain === 'all' ? 'work' : domain });
      setQuick('');
      if (tab !== 'now') setTab('now'); else await load();
    } catch (e2) { setErr(errText(e2)); } finally { setBusy(false); }
  }

  async function act(c: Commitment, verb: 'complete' | 'reopen') {
    try { await api('POST', `/api/t/commitments/${c.id}/${verb}`, undefined, c.revision); } catch (e) { setErr(e instanceof ApiErr && e.status === 409 ? 'That one changed somewhere else. The list has been refreshed.' : errText(e)); }
    await load();
  }

  const tabs = (['now', 'waiting', 'later', 'done'] as Tab[]).map((id) => ({
    id, label: `${{ now: 'Now', waiting: 'Waiting', later: 'Later', done: 'Done' }[id]}${data ? ` ${data.counts[id]}` : ''}`,
  }));

  return (
    <TeachingPage title="Commitments" breadcrumb="Everything you need to do, work and personal, in one list."
      actions={<><Tabs value={domain} options={DOMAIN_OPTS} onChange={setDomain} label="Work or personal" />
        <Btn onClick={() => setImporting(true)}><span class="inline-flex items-center gap-1.5 text-[13px]"><Upload size={14} />Import from a sheet</span></Btn></>}>
      <form onSubmit={add} class="flex gap-2" data-quick-add>
        <input class={inputCls + ' flex-1 !h-10'} value={quick} onInput={(e) => setQuick(val(e))} aria-label="Add a commitment"
          placeholder="Add something you need to do, then press Enter" maxLength={200} />
        <Btn kind="primary" type="submit" disabled={!quick.trim() || busy} class="!h-10"><span class="text-[13px]">Add</span></Btn>
      </form>
      <ErrorLine error={err} />
      {note && <Note tone="ok">{note}</Note>}
      <Tabs value={tab} options={tabs} onChange={setTab} label="Commitments" />

      <div class="rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] divide-y divide-[var(--color-border)]" data-commitments={tab}>
        {!data && !err && <div class="p-4 text-[13px] text-[var(--color-text-muted)]">Loading…</div>}
        {data && data.items.length === 0 && <div class="p-4 text-[13px] text-[var(--color-text-muted)]">{EMPTY[tab]}</div>}
        {data?.items.map((c) => {
          const finished = c.status === 'done' || c.status === 'dropped';
          return (
            <div key={c.id} class="flex items-start gap-3 px-3 md:px-4 py-3" data-commitment={c.title}>
              <button type="button" onClick={() => act(c, finished ? 'reopen' : 'complete')}
                aria-label={finished ? `Move back to Now: ${c.title}` : `Mark done: ${c.title}`} title={finished ? 'Move back to Now' : 'Mark done'}
                class={'mt-0.5 w-6 h-6 shrink-0 rounded-full border grid place-items-center ' + (finished ? 'bg-[var(--color-accent)] border-[var(--color-accent)]' : 'border-[var(--color-border)] hover:border-[var(--color-accent)]')}>
                {finished && <Check size={14} color="#ffffff" />}
              </button>
              <button type="button" onClick={() => setEditing(c)} class="min-w-0 flex-1 text-left" aria-label={`Open ${c.title}`}>
                <span class={'block text-[14px] font-semibold leading-snug break-words ' + (finished ? 'text-[var(--color-text-muted)] line-through' : 'text-[var(--color-text)]')}>{c.next_action || c.title}</span>
                {c.next_action && c.next_action !== c.title && <span class="block text-[12.5px] text-[var(--color-text-muted)] mt-0.5 break-words">{c.title}</span>}
                <span class="mt-1.5 flex flex-wrap items-center gap-1.5">
                  {c.deadline && <DeadlineChip iso={c.deadline} done={finished} />}
                  {c.status === 'dropped' && <Chip tone="neutral">Dropped</Chip>}
                  {c.kind !== 'task' && <Chip tone="neutral">{KIND_LABEL[c.kind]}</Chip>}
                  {domain === 'all' && <Chip tone="neutral">{c.domain === 'work' ? 'Work' : 'Personal'}</Chip>}
                  {c.waiting_on && <Chip tone="watch">Waiting on {c.waiting_on}</Chip>}
                  {c.follow_up_on && !finished && <Chip tone="neutral">Follow up {shortDate(c.follow_up_on)}</Chip>}
                  {c.needs_planning && <Chip tone="neutral">Needs planning</Chip>}
                  <span class="text-[11.5px] text-[var(--color-text-faint)]">{ageText(c.created_at)}</span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
      <p class="text-[12px] text-[var(--color-text-muted)]">Keep one next action on each line. A project is one line with the step you can do next, not a list of every step.</p>

      {editing && <EditDrawer c={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); }} />}
      {importing && <ImportDrawer defaultDomain={domain === 'all' ? 'work' : domain} onClose={() => setImporting(false)}
        onDone={async (n, skipped) => { setImporting(false); setNote(`Imported ${n} ${n === 1 ? 'row' : 'rows'}${skipped ? `. ${skipped} already on your list ${skipped === 1 ? 'was' : 'were'} skipped` : ''}.`); await load(); }} />}
    </TeachingPage>
  );
}

// ── Drawer shell: a side panel on laptop, the whole screen on phone ───

function Drawer({ title, onClose, children, wide }: { title: string; onClose: () => void; children: preact.ComponentChildren; wide?: boolean }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <div class="fixed inset-0 z-[60] flex justify-end" role="dialog" aria-modal="true" aria-label={title}>
      <div class="absolute inset-0 bg-black/40" onClick={onClose} />
      <div class={'relative h-full w-full bg-[var(--color-bg)] border-l border-[var(--color-border)] shadow-xl flex flex-col ' + (wide ? 'md:max-w-[820px]' : 'md:max-w-[460px]')}>
        <div class="flex items-center justify-between px-4 h-14 border-b border-[var(--color-border)] shrink-0">
          <div class="text-[15px] font-semibold text-[var(--color-text)]">{title}</div>
          <button type="button" onClick={onClose} aria-label="Close" class="w-8 h-8 grid place-items-center rounded-md text-[var(--color-text-muted)] hover:bg-[var(--color-elevated)]"><X size={18} /></button>
        </div>
        <div class="flex-1 min-h-0 overflow-y-auto p-4 flex flex-col gap-3">{children}</div>
      </div>
    </div>
  );
}

const STATUS_OPTS: { id: Status; label: string }[] = [
  { id: 'active', label: 'Now' }, { id: 'waiting', label: 'Waiting' }, { id: 'deferred', label: 'Later' }, { id: 'done', label: 'Done' }, { id: 'dropped', label: 'Dropped' },
];

function EditDrawer({ c, onClose, onSaved }: { c: Commitment; onClose: () => void; onSaved: () => void | Promise<void> }) {
  const [f, setF] = useState({ ...c });
  const [err, setErr] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [stale, setStale] = useState(false);
  const set = <K extends keyof Commitment>(k: K, v: Commitment[K]) => setF({ ...f, [k]: v });

  const fail = (e: unknown) => {
    if (e instanceof ApiErr && e.status === 409) { setStale(true); setErr('This was changed somewhere else since you opened it. Close and open it again to see the latest.'); return; }
    setErr(errText(e)); setFields(e instanceof ApiErr ? e.fields ?? {} : {});
  };
  async function save(e: Event) {
    e.preventDefault(); setErr(null); setFields({});
    try {
      await api('PATCH', `/api/t/commitments/${c.id}`, {
        title: f.title, next_action: f.next_action, outcome: f.outcome, domain: f.domain, kind: f.kind, status: f.status, deadline: f.deadline || null,
        waiting_on: f.waiting_on, follow_up_on: f.follow_up_on || null, delegated_to: f.delegated_to, needs_planning: f.needs_planning, notes: f.notes,
      }, c.revision);
      await onSaved();
    } catch (e2) { fail(e2); }
  }
  async function remove() {
    try { await api('DELETE', `/api/t/commitments/${c.id}`, undefined, c.revision); await onSaved(); } catch (e2) { fail(e2); }
  }

  return (
    <Drawer title="Edit commitment" onClose={onClose}>
      <form onSubmit={save} class="flex flex-col gap-3" data-edit-commitment>
        <Field label="Next action (the one step you can do next)" error={fields.next_action}>
          <input class={inputCls} value={f.next_action} onInput={(e) => set('next_action', val(e))} maxLength={300} />
        </Field>
        <Field label="Task or project" error={fields.title}>
          <input class={inputCls} value={f.title} onInput={(e) => set('title', val(e))} maxLength={200} />
        </Field>
        <Field label="What does done look like?" error={fields.outcome}>
          <input class={inputCls} value={f.outcome} onInput={(e) => set('outcome', val(e))} maxLength={500} />
        </Field>
        <div class="grid grid-cols-2 gap-3">
          <Field label="Where it sits">
            <select class={selectCls} value={f.status} onChange={(e) => set('status', val(e) as Status)}>{STATUS_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}</select>
          </Field>
          <Field label="Work or personal">
            <select class={selectCls} value={f.domain} onChange={(e) => set('domain', val(e) as Domain)}><option value="work">Work</option><option value="personal">Personal</option></select>
          </Field>
          <Field label="Type">
            <select class={selectCls} value={f.kind} onChange={(e) => set('kind', val(e) as Kind)}>{(Object.keys(KIND_LABEL) as Kind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</select>
          </Field>
          <Field label="Deadline" error={fields.deadline}>
            <input type="date" class={inputCls} value={f.deadline ?? ''} onInput={(e) => set('deadline', val(e) || null)} />
          </Field>
          <Field label="Waiting on (who or what)" error={fields.waiting_on}>
            <input class={inputCls} value={f.waiting_on} onInput={(e) => set('waiting_on', val(e))} maxLength={200} />
          </Field>
          <Field label="Follow up on" error={fields.follow_up_on}>
            <input type="date" class={inputCls} value={f.follow_up_on ?? ''} onInput={(e) => set('follow_up_on', val(e) || null)} />
          </Field>
          <Field label="Delegated to" error={fields.delegated_to} class="col-span-2">
            <input class={inputCls} value={f.delegated_to} onInput={(e) => set('delegated_to', val(e))} maxLength={200} />
          </Field>
        </div>
        <label class="flex items-center gap-2 text-[13px] text-[var(--color-text)]">
          <input type="checkbox" checked={f.needs_planning} onChange={(e) => set('needs_planning', (e.target as HTMLInputElement).checked)} />
          Needs proper planning before I start
        </label>
        <Field label="Notes" error={fields.notes}>
          <textarea class={textareaCls} rows={4} value={f.notes} onInput={(e) => set('notes', val(e))} maxLength={4000} />
        </Field>
        <ErrorLine error={err} />
        <div class="flex flex-wrap items-center gap-2">
          <Btn kind="primary" type="submit" disabled={stale}><span class="text-[13px]">Save</span></Btn>
          <Btn onClick={onClose}><span class="text-[13px]">Cancel</span></Btn>
          <Btn kind="danger" onClick={remove} disabled={stale} class="ml-auto" title="Takes it off your list. It is kept in the record."><span class="text-[13px]">Remove</span></Btn>
        </div>
        <div class="text-[11.5px] text-[var(--color-text-faint)]">
          {c.source === 'import' ? 'Imported from your sheet. ' : c.source === 'manager' ? 'Added by your Manager helper. ' : ''}Added {new Date(c.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}.
        </div>
      </form>
    </Drawer>
  );
}

// ── Import: paste, match the columns, check, import ───────────────────

function ImportDrawer({ defaultDomain, onClose, onDone }: { defaultDomain: Domain; onClose: () => void; onDone: (added: number, skipped: number) => void | Promise<void> }) {
  const [text, setText] = useState('');
  const [dom, setDom] = useState<Domain>(defaultDomain);
  const [p, setP] = useState<ImportPreview | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function preview(mapping?: (ImportField | null)[]) {
    setErr(null); setBusy(true);
    try { setP(await api<ImportPreview>('POST', '/api/t/commitments/import/preview', { text, defaultDomain: dom, ...(mapping ? { mapping } : {}) })); }
    catch (e) {
      // No heading matched: show the columns so the teacher can match them by hand.
      if (!mapping && e instanceof ApiErr && e.fields?.mapping) {
        const first = text.replace(/\r/g, '').split('\n')[0] ?? '';
        const headers = first.split(first.includes('\t') ? '\t' : ',').map((h) => h.trim());
        setP({ delimiter: first.includes('\t') ? 'tab' : 'comma', headers, mapping: headers.map(() => null), rows: [], summary: { total: 0, ready: 0, duplicates: 0, invalid: 0 } });
        setErr('Choose which column holds the task or project.');
      } else setErr(errText(e));
    } finally { setBusy(false); }
  }
  function remap(col: number, field: ImportField | null) {
    if (!p) return;
    // A field can fill from one column only: taking it here frees it elsewhere.
    const mapping = p.mapping.map((m, i) => (i === col ? field : m === field ? null : m));
    setP({ ...p, mapping });
    void preview(mapping);
  }
  async function run() {
    if (!p) return;
    setErr(null); setBusy(true);
    try {
      const r = await api<{ added: Commitment[]; skipped: number }>('POST', '/api/t/commitments/import', { text, defaultDomain: dom, mapping: p.mapping });
      await onDone(r.added.length, r.skipped);
    } catch (e) { setErr(errText(e)); } finally { setBusy(false); }
  }

  return (
    <Drawer title="Import from a sheet" onClose={onClose} wide>
      {!p && (
        <div class="flex flex-col gap-3" data-import-paste>
          <p class="text-[13px] text-[var(--color-text-muted)]">In your sheet, select the rows you want including the heading row, copy them, and paste here. Nothing is saved until you have checked it on the next screen.</p>
          <textarea class={textareaCls + ' font-mono !text-[12px]'} rows={10} value={text} onInput={(e) => setText(val(e))} aria-label="Rows copied from your sheet" placeholder={'Task\tNext action\tDeadline\tStatus'} />
          <Field label="Rows with no Work or Personal column are" class="max-w-[280px]">
            <select class={selectCls} value={dom} onChange={(e) => setDom(val(e) as Domain)}><option value="work">Work</option><option value="personal">Personal</option></select>
          </Field>
          <ErrorLine error={err} />
          <div class="flex gap-2"><Btn kind="primary" disabled={!text.trim() || busy} onClick={() => preview()}><span class="text-[13px]">Check the rows</span></Btn><Btn onClick={onClose}><span class="text-[13px]">Cancel</span></Btn></div>
        </div>
      )}
      {p && (
        <div class="flex flex-col gap-3" data-import-preview>
          <Card title="Match your columns" sub="We matched what we could from the headings. Change any that are wrong; columns set to Ignore are left out.">
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {p.headers.map((h, i) => (
                <label key={i} class="flex items-center gap-2 text-[12.5px] text-[var(--color-text)] min-w-0">
                  <span class="flex-1 min-w-0 truncate" title={h}>{h || `Column ${i + 1}`}</span>
                  <select class={selectCls + ' w-[168px] shrink-0'} value={p.mapping[i] ?? ''} aria-label={`Column ${h || i + 1} is`} onChange={(e) => remap(i, (val(e) || null) as ImportField | null)}>
                    <option value="">Ignore</option>
                    {IMPORT_FIELDS.map((f) => <option key={f} value={f}>{FIELD_LABEL[f]}</option>)}
                  </select>
                </label>
              ))}
            </div>
          </Card>
          <ErrorLine error={err} />
          {p.rows.length > 0 && (
            <>
              <div class="text-[13px] text-[var(--color-text)]" data-import-summary>
                <b>{p.summary.ready}</b> {p.summary.ready === 1 ? 'row' : 'rows'} ready to import
                {p.summary.duplicates > 0 && <>, {p.summary.duplicates} already on your list</>}
                {p.summary.invalid > 0 && <>, {p.summary.invalid} with no task</>}.
              </div>
              <TableCard>
                <table class="w-full border-collapse">
                  <thead><tr><th class={thCls}>Row</th><th class={thCls}>Next action</th><th class={thCls}>Task or project</th><th class={thCls}>Deadline</th><th class={thCls}>Goes to</th><th class={thCls}>Check</th></tr></thead>
                  <tbody>
                    {p.rows.slice(0, 200).map((r) => (
                      <tr key={r.line} class={'border-b border-[var(--color-border)] last:border-0 ' + (r.skip ? 'opacity-55' : '')}>
                        <td class={tdCls + ' tabular-nums'}>{r.line}</td>
                        <td class={tdCls + ' min-w-[160px]'}>{r.values.next_action}</td>
                        <td class={tdCls + ' min-w-[140px]'}>{r.values.title}</td>
                        <td class={tdCls + ' whitespace-nowrap'}>{r.values.deadline ? shortDate(r.values.deadline) : ''}</td>
                        <td class={tdCls + ' whitespace-nowrap'}>{r.skip ? '' : `${STATUS_OPTS.find((o) => o.id === (r.values.status ?? 'active'))!.label}, ${r.values.domain === 'personal' ? 'Personal' : 'Work'}`}</td>
                        <td class={tdCls + ' min-w-[160px]'}>
                          {r.duplicate ? <Chip tone="neutral">Already on your list</Chip> : !r.values.title ? <Chip tone="reteach">No task</Chip> : r.problems.length ? <span class="text-[12px] text-[var(--color-text-muted)]">{r.problems.join('. ')}</span> : <Chip tone="strong">Ready</Chip>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
              {p.rows.length > 200 && <div class="text-[12px] text-[var(--color-text-muted)]">Showing the first 200 rows of {p.rows.length}. All of them are imported.</div>}
            </>
          )}
          <div class="flex flex-wrap gap-2">
            <Btn kind="primary" disabled={busy || p.summary.ready === 0} onClick={run}><span class="text-[13px]">Import {p.summary.ready} {p.summary.ready === 1 ? 'row' : 'rows'}</span></Btn>
            <Btn onClick={() => { setP(null); setErr(null); }}><span class="text-[13px]">Back</span></Btn>
          </div>
        </div>
      )}
    </Drawer>
  );
}
